import { useQuery } from '@apollo/client';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { includes, map } from 'ramda';
import React, { FC, useContext } from 'react';
import { useDeepCompareEffect } from 'react-use';

import { ICurrentMemberWithSubscriptions } from '@hooks/useSubscriptionInfo';

import { keysToCamel } from '@utils/butter';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import CURRENT_MEMBER_WITH_SUBSCRIPTIONS from '@graphql/queries/currentMemberAllSubscriptions.gql';

import MemberAppointmentSelectionStepComponent, {
	IMemberAppointmentSelectionStepPageContent,
} from './MemberAppointmentSelectionStep.component';

interface IAppointmentSelectionStepPageProps extends IFlowStepProps {
	flowId: string;
}

const MemberAppointmentSelectionStepPage: FC<IAppointmentSelectionStepPageProps> = ({
	flowId,
	path,
	stepKey,
}) => {
	const { pageContent } = useStaticQuery<Record<string, string>>(graphql`
		{
			pageContent: butterPage(slug: { eq: "member-appointment-selection" }) {
				appointment_selection_header
				appointment_selection_description
				medication_description
				in_person_header
				in_person_description
				appointment_header
				appointment_subheader
				appointment_description
				location_subheader
				location_description
				cancellation_header
				cancellation_description
				edit_label
				interpreter_info
				past_membership_cancel_date {
					header
					subheader
					description
					cta
					secondary_cta
				}
			}
		}
	`);

	const {
		loading: memberSubscriptionsLoading,
		data: memberSubscriptionsData,
		error: memberSubscriptionsError,
	} = useQuery<ICurrentMemberWithSubscriptions>(CURRENT_MEMBER_WITH_SUBSCRIPTIONS);

	useDeepCompareEffect(() => {
		if (memberSubscriptionsLoading) {
			return;
		}

		const allSubNames = map(
			(n) => n.subscriptionType,
			memberSubscriptionsData?.currentMember.memberSubscriptions?.nodes ?? []
		);

		const hasMembership = includes('MEMBERSHIP', allSubNames);
		if (!hasMembership) {
			navigate('../check_membership', {
				state: { onReturn: '../appointment_selection', requireOnboarding: true },
			});
			return;
		}
	}, [
		memberSubscriptionsLoading,
		memberSubscriptionsData?.currentMember.memberSubscriptions?.nodes,
	]);

	const { existingFormData, handleFormSubmission, handleNavBack } = useContext(FlowContext);

	const refinedContent: IMemberAppointmentSelectionStepPageContent = keysToCamel(
		pageContent
	) as IMemberAppointmentSelectionStepPageContent;

	return (
		<MemberAppointmentSelectionStepComponent
			pageContent={refinedContent}
			path={path}
			stepKey={stepKey}
			flowId={flowId}
			existingFormData={existingFormData}
			handleFormSubmission={handleFormSubmission}
			handleNavBack={handleNavBack}
		/>
	);
};

export default MemberAppointmentSelectionStepPage;
